import { AntDesign } from "@expo/vector-icons";
import { router, useFocusEffect, useGlobalSearchParams } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Text, Pressable, ScrollView } from "react-native";

import Title from "@/components/Title";
import Accounts from "@/components/management/Accounts";
import Counterparties from "@/components/management/Counterparties";
import Documents from "@/components/management/Documents";
import GeneralInformation from "@/components/management/GeneralInformation";
import Individuals from "@/components/management/Individuals";
import { sendRequest } from "@/utils/ApiUtil";

export default function Customer() {
  const [currentOption, setCurrentOption] = useState("INFORMATION");
  const [currentHoveredOption, setCurrentHoveredOption] = useState("");
  const [layer2Id, setLayer2Id] = useState("");
  const [customerInformation, setCustomerInformation] = useState<any>({});
  const [documents, setDocuments] = useState([]);
  const [customerType, setCustomerType] = useState("");
  const { t } = useTranslation();
  const params = useGlobalSearchParams();

  useFocusEffect(
    React.useCallback(() => {
      const idAndType =
        typeof params.id === "string" ? params.id.split("_") : [];
      setCustomerType(idAndType[1]);
      fetchCustomerDetails(idAndType[0]);
      return () => {
        setDocuments([]);
        setCustomerInformation({});
        setCurrentOption("INFORMATION");
        setLayer2Id("");
      };
    }, [params.id])
  );

  const fetchCustomerDetails = async (customerId: string) => {
    try {
      const request = await sendRequest(
        "get",
        `/admin/customer`,
        { customerId },
        true
      );
      if (request) {
        setLayer2Id(request[0].application?.applicationId);
        setDocuments(request[0].application?.documents);
        setCustomerInformation(request[0]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center px-4 py-2 bg-white1">
      <View className="flex-col w-full items-center">
        <View className="my-2">
          <Title text={t("management/customer:title")} />
        </View>
        <View className="w-full items-start">
          <Pressable
            onPress={() => {
              router.replace("/management/dashboard");
            }}
          >
            <AntDesign name="arrowleft" size={24} />
          </Pressable>
        </View>
        <View className="w-full flex-col items-start mt-4">
          <ScrollView horizontal className="flex-row">
            <Pressable
              className={`mr-4 ${
                currentOption === "INFORMATION" ? "border-b border-purple1" : ""
              } ${
                currentHoveredOption === "INFORMATION"
                  ? "border-b border-midgray"
                  : ""
              }`}
              onPress={() => setCurrentOption("INFORMATION")}
              onHoverIn={() => setCurrentHoveredOption("INFORMATION")}
              onHoverOut={() => setCurrentHoveredOption("")}
            >
              <Text
                className={`text-lg ${
                  currentOption === "INFORMATION" ? "text-purple1" : ""
                } ${
                  currentHoveredOption === "INFORMATION"
                    ? "text-darkergray"
                    : ""
                }`}
              >
                {t("management/customer:options.information")}
              </Text>
            </Pressable>
            {customerType === "corporation" && (
              <Pressable
                className={`mr-4 ${
                  currentOption === "INDIVIDUALS"
                    ? "border-b border-purple1"
                    : ""
                } ${
                  currentHoveredOption === "INDIVIDUALS"
                    ? "border-b border-midgray"
                    : ""
                }`}
                onPress={() => setCurrentOption("INDIVIDUALS")}
                onHoverIn={() => setCurrentHoveredOption("INDIVIDUALS")}
                onHoverOut={() => setCurrentHoveredOption("")}
              >
                <Text
                  className={`text-lg ${
                    currentOption === "INDIVIDUALS" ? "text-purple1" : ""
                  } ${
                    currentHoveredOption === "INDIVIDUALS"
                      ? "text-darkergray"
                      : ""
                  }`}
                >
                  {t("management/customer:options.individuals")}
                </Text>
              </Pressable>
            )}
            <Pressable
              className={`mr-4 ${
                currentOption === "ACCOUNTS" ? "border-b border-purple1" : ""
              } ${
                currentHoveredOption === "ACCOUNTS"
                  ? "border-b border-midgray"
                  : ""
              }`}
              onPress={() => setCurrentOption("ACCOUNTS")}
              onHoverIn={() => setCurrentHoveredOption("ACCOUNTS")}
              onHoverOut={() => setCurrentHoveredOption("")}
            >
              <Text
                className={`text-lg ${
                  currentOption === "ACCOUNTS" ? "text-purple1" : ""
                } ${
                  currentHoveredOption === "ACCOUNTS" ? "text-darkergray" : ""
                }`}
              >
                {t("management/customer:options.accounts")}
              </Text>
            </Pressable>
            <Pressable
              className={`mr-4 ${
                currentOption === "DOCUMENTS" ? "border-b border-purple1" : ""
              } ${
                currentHoveredOption === "DOCUMENTS"
                  ? "border-b border-midgray"
                  : ""
              }`}
              onPress={() => setCurrentOption("DOCUMENTS")}
              onHoverIn={() => setCurrentHoveredOption("DOCUMENTS")}
              onHoverOut={() => setCurrentHoveredOption("")}
            >
              <Text
                className={`text-lg ${
                  currentOption === "DOCUMENTS" ? "text-purple1" : ""
                } ${
                  currentHoveredOption === "DOCUMENTS" ? "text-darkergray" : ""
                }`}
              >
                {t("management/customer:options.documents")}
              </Text>
            </Pressable>
            <Pressable
              className={`mr-4 ${
                currentOption === "COUNTERPARTIES"
                  ? "border-b border-purple1"
                  : ""
              } ${
                currentHoveredOption === "COUNTERPARTIES"
                  ? "border-b border-midgray"
                  : ""
              }`}
              onPress={() => setCurrentOption("COUNTERPARTIES")}
              onHoverIn={() => setCurrentHoveredOption("COUNTERPARTIES")}
              onHoverOut={() => setCurrentHoveredOption("")}
            >
              <Text
                className={`text-lg ${
                  currentOption === "COUNTERPARTIES" ? "text-purple1" : ""
                } ${
                  currentHoveredOption === "COUNTERPARTIES"
                    ? "text-darkergray"
                    : ""
                }`}
              >
                {t("management/customer:options.counterparties")}
              </Text>
            </Pressable>
          </ScrollView>
        </View>
        <View className="w-full items-center mt-6">
          {Object.keys(customerInformation).length > 0 && (
            <>
              {currentOption === "INFORMATION" && (
                <GeneralInformation
                  data={
                    (customerInformation.application
                      .customerDetailsCorporation ||
                      customerInformation.application
                        .customerDetailsIndividual) ??
                    {}
                  }
                />
              )}
              {currentOption === "INDIVIDUALS" && (
                <Individuals
                  data={
                    customerInformation.application.customerDetailsCorporation
                      .individuals ?? {}
                  }
                />
              )}
              {currentOption === "ACCOUNTS" && (
                <Accounts
                  data={customerInformation ? customerInformation : {}}
                />
              )}
              {currentOption === "DOCUMENTS" && (
                <Documents
                  layer2Id={layer2Id}
                  data={documents ? documents : []}
                />
              )}
              {currentOption === "COUNTERPARTIES" && (
                <Counterparties
                  data={customerInformation ? customerInformation : {}}
                />
              )}
            </>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
